import type { Logger, LoggerRole } from '@zep/logger';

import { shouldLoadGoogleTagManager } from '@/consts/env';

type GtmMarketingEventName = keyof typeof GTM_MARKETING_EVENT_PARAM_ALLOWLIST;
type DataLayerValue = string | number | boolean;
type DataLayerEvent = {
  event: GtmMarketingEventName;
} & Record<string, DataLayerValue>;

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/**
 * GTM `dataLayer` 로 내보낼 이벤트와 **이벤트별로 허용할 파라미터** 목록.
 * 허용목록에 없는 키는 잘라낸다(PII 유출 방지). 이벤트 자체가 없으면 아예 내보내지 않는다.
 *
 * `tracking_id` 는 클릭 ↔ 문의폼 제출을 잇는 1회성 익명 UUID 라 PII 가 아니며,
 * 여기 없으면 잘려나가 GTM 쪽에서는 전환 조인이 불가능해진다.
 */
const GTM_MARKETING_EVENT_PARAM_ALLOWLIST = {
  signup_complete: ['userType', 'type'],
  create_complete: ['create_type'],
  click_school_pro_inquiry: ['tracking_id'],
  quiz_pricing_basic_inquiry_click: ['tracking_id'],
  click_school_budget_inquiry: ['tracking_id'],
} as const;

const isGtmMarketingEventName = (
  eventName: string,
): eventName is GtmMarketingEventName =>
  Object.prototype.hasOwnProperty.call(
    GTM_MARKETING_EVENT_PARAM_ALLOWLIST,
    eventName,
  );

const isDataLayerValue = (value: unknown): value is DataLayerValue =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

const getBrowserDataLayer = () => {
  if (typeof window === 'undefined') return undefined;

  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
};

export class GtmMarketingEventLogger implements Logger {
  public name = 'gtmMarketingEvent';
  public role: LoggerRole = 'tracking';
  private readonly _isEnabled: boolean;
  private readonly _getDataLayer: () => unknown[] | undefined;

  constructor({
    isEnabled = shouldLoadGoogleTagManager,
    getDataLayer = getBrowserDataLayer,
  }: {
    isEnabled?: boolean;
    getDataLayer?: () => unknown[] | undefined;
  } = {}) {
    this._isEnabled = isEnabled;
    this._getDataLayer = getDataLayer;
  }

  log(message: string, params?: Record<string, unknown>) {
    this.track(message, params);
  }

  track(message: string, params: Record<string, unknown> = {}) {
    if (!this._isEnabled || !isGtmMarketingEventName(message)) return;

    const dataLayer = this._getDataLayer();
    if (!dataLayer) return;

    const event = GTM_MARKETING_EVENT_PARAM_ALLOWLIST[
      message
    ].reduce<DataLayerEvent>(
      (acc, key) => {
        const value = params[key];
        if (isDataLayerValue(value)) {
          acc[key] = value;
        }
        return acc;
      },
      { event: message },
    );

    dataLayer.push(event);
  }
}
